/** Schedule: my calendar, plus a heat map / list of coming days where deputies will be needed. */
import { useState } from 'react';
import { api } from '../shared/api.ts';
import { useRouter } from '../shared/router.tsx';
import { hours, ScheduleCalendar } from '../shared/ScheduleCalendar.tsx';
import { Card, ErrorBox, Notice, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { MySetOutsCard } from './SetOuts.tsx';

const LEVEL: Record<string, string> = { closed: 'Closed', low: 'Covered', medium: 'Needed', high: 'Needed most' };
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const md = (d: string) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
const dayName = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });

export function SchedulePage({ online }: { online: boolean }) {
  const { navigate } = useRouter();
  const [month, setMonth] = useState<string | null>(null);
  const [view, setView] = useState<'map' | 'list'>('map');
  const [pick, setPick] = useState<string | null>(null);
  const home = useLoad(() => (online ? api.get<any>(`/api/me/home${month ? `?month=${month}` : ''}`) : Promise.resolve(null)), [month, online]);
  const needs = useLoad(() => (online ? api.get<any>('/api/me/needs?days=28') : Promise.resolve(null)), [online]);
  const a = useAction();
  if (!online) return <Notice tone="warn">Schedule needs a connection.</Notice>;
  const n = needs.data;
  const h = home.data;
  const reload = () => (home.reload(), needs.reload());
  const work = (day: string) =>
    a.run(async () => {
      const s = h?.suggestions?.[day];
      await api.post(`/api/schedule/${h.user.id}/${day}`, { kind: 'working', start: s?.start ?? '17:00', end: s?.end ?? '21:00' });
      reload();
    });
  // Pad the heat map to whole weeks (Sun–Sat).
  const cells: (any | null)[] = n ? [...Array(n.days[0].weekday).fill(null), ...n.days] : [];
  while (cells.length % 7) cells.push(null);
  const needed = n ? n.days.filter((d: any) => d.level === 'high' || d.level === 'medium') : [];
  const sel = n?.days.find((d: any) => d.day === pick) ?? null;
  return (
    <div className="stack home">
      <h1>Schedule</h1>
      <MySetOutsCard online={online} />

      <Card
        title="Where deputies are needed · next 4 weeks"
        actions={
          <div className="seg" role="group" aria-label="View">
            <button className={view === 'map' ? 'active' : ''} onClick={() => setView('map')}>
              Heat map
            </button>
            <button className={view === 'list' ? 'active' : ''} onClick={() => setView('list')}>
              List
            </button>
          </div>
        }
      >
        <ErrorBox error={needs.error ?? a.error} />
        {!n ? (
          <Spinner />
        ) : (
          <>
            <p className="small muted">
              Papers due = open papers whose service cutoff is that day or within the {n.needWindowDays} days after it — they need attempts then. Compared with deputies scheduled working (flexible counts half).
              {n.overdue > 0 && <strong> {n.overdue} already past cutoff (counted today).</strong>} {n.unassigned} waiting to be assigned · {n.noDeadline} open papers with no court date.
            </p>
            {view === 'map' ? (
              <>
                <div className="hm" role="grid" aria-label="Need for deputies by day">
                  {WD.map((w) => (
                    <div key={w} className="hm-wd" role="columnheader">
                      {w}
                    </div>
                  ))}
                  {cells.map((d, i) =>
                    d ? (
                      <button
                        key={d.day}
                        role="gridcell"
                        className={`hm-c lv-${d.level} ${d.day === n.today ? 'today' : ''} ${pick === d.day ? 'sel' : ''}`}
                        onClick={() => setPick(pick === d.day ? null : d.day)}
                        title={`${dayName(d.day)} — ${d.blocked ?? `${LEVEL[d.level]}: ${d.papersDue} papers due, ${d.working} working, ${d.flexible} flexible`}${d.mine ? ` · you: ${d.mine}` : ''}`}
                        aria-label={`${dayName(d.day)}: ${d.blocked ?? `${LEVEL[d.level]}, ${d.papersDue} papers due, ${d.working} working`}`}
                      >
                        <span className="hm-d">{md(d.day)}</span>
                        <span className="hm-n">{d.level === 'closed' ? '—' : d.papersDue}</span>
                        <span className="hm-l">{LEVEL[d.level]}</span>
                        {d.mine && d.mine !== 'off' && <span className="hm-me" title="You're scheduled">●</span>}
                      </button>
                    ) : (
                      <div key={`x${i}`} className="hm-c empty" />
                    ),
                  )}
                </div>
                <div className="hm-legend small">
                  <span><i className="lv-low" /> Covered</span>
                  <span><i className="lv-medium" /> Needed</span>
                  <span><i className="lv-high" /> Needed most</span>
                  <span><i className="lv-closed" /> Closed (Sunday / holiday)</span>
                  <span>● you’re scheduled</span>
                </div>
                {sel && (
                  <div className="hm-detail">
                    <strong>{dayName(sel.day)}</strong>
                    {sel.blocked ? (
                      <span> — {sel.blocked}</span>
                    ) : (
                      <>
                        <span>
                          {' '}
                          — {sel.papersDue} papers due · {sel.working} working · {sel.flexible} flexible · you: {sel.mine ?? 'not set'}
                        </span>
                        {sel.mine !== 'working' && (
                          <button className="btn sm primary" disabled={a.busy} onClick={() => work(sel.day)}>
                            I can work this day
                          </button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            ) : needed.length === 0 ? (
              <p className="muted">No days short of deputies in the next 4 weeks.</p>
            ) : (
              <ul className="plain need-list">
                {needed.map((d: any) => (
                  <li key={d.day} className={`need lv-${d.level}`}>
                    <span className="need-badge">{LEVEL[d.level]}</span>
                    <strong>{dayName(d.day)}</strong>
                    <span className="grow small">
                      {d.papersDue} papers due · {d.working} working · {d.flexible} flexible
                    </span>
                    {d.mine === 'working' ? (
                      <span className="small ok-text">You’re working</span>
                    ) : (
                      <button className="btn sm" disabled={a.busy} onClick={() => work(d.day)}>
                        I can work
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Card>

      <Card title="My calendar">
        <ErrorBox error={home.error} />
        {!h ? (
          <Spinner />
        ) : (
          <>
            {(() => {
              const up = h.schedule.filter((e: any) => e.day >= h.today && e.kind !== 'off')[0];
              return up ? (
                <p className="small">
                  Next scheduled: <strong>{dayName(up.day)} · {up.kind === 'working' ? hours(up) : 'Flexible'}</strong>
                </p>
              ) : null;
            })()}
            <p className="small muted">Tap a day to say you’re working (and when), flexible, or off. Days your supervisor set are marked and locked.</p>
            {(h.court ?? []).filter((c: any) => c.ends_at > new Date().toISOString()).slice(0, 3).map((c: any) => (
              <button key={c.id + c.role} type="button" className="court-next" onClick={() => navigate(`/field/court/${c.id}`)}>
                <strong>{new Date(c.starts_at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</strong>
                <span>{c.role === 'security' ? 'Court security' : c.role === 'testifying' ? 'Testifying' : c.role === 'transport' ? 'Prisoner transport' : 'Court'} · {c.court_name}{c.courtroom ? `, ${c.courtroom}` : ''}{c.defendant ? ` · ${c.defendant}` : ''}</span>
              </button>
            ))}
            <ScheduleCalendar userId={h.user.id} month={h.month} today={h.today} entries={h.schedule} suggestions={h.suggestions} supervisor={false} onMonth={setMonth} onSaved={reload} court={h.court} onCourt={(id) => navigate(`/field/court/${id}`)} />
          </>
        )}
      </Card>
    </div>
  );
}
